import { atom } from 'jotai';
import { User_Base } from '~/common/typescript/models/User';
import { UserLocation_Base } from '~/common/typescript/models/UserLocation';
import { AC_Project__User_with_Roles } from '~/common/typescript/services/access_control/AC_Project';
import { currentProjectAtom } from '~/stores/ProjectStore';
import { ProjectAPIs } from '~/common/typescript/apis/ProjectAPIs';

export const projectMembersLocationAtom = atom<UserLocation_Base[]>([]);

export const memberDisplayedBoundsAtom = atom<string | null>(null);

export const showMemberLabelsAtom = atom<boolean>(true); // Temporarily true for testing

export const ZOOM_TO_MEMBER_EVENT = 'geocloud:zoom-to-member';

export interface ZoomToMemberEventDetail {
  userId: number;
  fullname?: string;
  username?: string;
}

export const dispatchZoomToMember = (detail: ZoomToMemberEventDetail) => {
  if (!detail?.userId) return;
  window.dispatchEvent(new CustomEvent<ZoomToMemberEventDetail>(ZOOM_TO_MEMBER_EVENT, { detail }));
};

export const parseMemberLocationCoords = (
  rawLocation: any,
): { lng: number; lat: number; accuracy?: number } | null => {
  if (!rawLocation) return null;
  try {
    const loc = typeof rawLocation === 'string' ? JSON.parse(rawLocation) : rawLocation;
    if (!loc) return null;
    if (loc.type === 'Point' && Array.isArray(loc.coordinates) && loc.coordinates.length >= 2) {
      return {
        lng: Number(loc.coordinates[0]),
        lat: Number(loc.coordinates[1]),
        accuracy: typeof loc.accuracy === 'number' ? loc.accuracy : undefined,
      };
    }
    const lng = loc.longitude ?? loc.lng;
    const lat = loc.latitude ?? loc.lat;
    if (typeof lng === 'number' && typeof lat === 'number') {
      return {
        lng,
        lat,
        accuracy: typeof loc.accuracy === 'number' ? loc.accuracy : undefined,
      };
    }
  } catch {
    // ignore malformed location
  }
  return null;
};

// Base atom for project members with roles - shared across components
export const projectMembersWithRolesAtom = atom<AC_Project__User_with_Roles[]>([]);

// Map of project members indexed by user ID for instant O(1) lookup
export const projectMembersMapAtom = atom<Record<number, User_Base>>((get) => {
  const members = get(projectMembersWithRolesAtom);
  const currentProject = get(currentProjectAtom);
  const map: Record<number, User_Base> = {};
  if (currentProject?.owner?.id) {
    map[currentProject.owner.id] = currentProject.owner;
  }
  members.forEach((m) => {
    if (m?.user?.id) {
      map[m.user.id] = m.user;
    }
  });
  return map;
});

// Derived atom that automatically fetches members when project changes
export const projectMembersWithRolesAsyncAtom = atom(
  (get) => get(projectMembersWithRolesAtom),
  async (get, set) => {
    const currentProject = get(currentProjectAtom);
    if (!currentProject?.id) {
      set(projectMembersWithRolesAtom, []);
      return;
    }

    try {
      const membersWithRoles = await ProjectAPIs.remote_getUsers_with_Roles({
        projectId: currentProject.id,
      });
      set(projectMembersWithRolesAtom, membersWithRoles);
    } catch (error) {
      console.error('Error fetching project members:', error);
      set(projectMembersWithRolesAtom, []);
    }
  }
);